import { AdvancedDynamicTexture, Button, Control, Line } from '@babylonjs/gui'
import { createSelector } from '@reduxjs/toolkit'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
} from 'react'
import { validate as isValidUUID } from 'uuid'
import store, { AppDispatch, RootState } from '../../store'
import { ConstructionTab } from '../../reducers/construction'
import { BuildingEditingTool } from '../../models/Construction'
import { SceneManager } from '../sceneManager'
import { isDoorMesh, isWallMesh, isWindowMesh } from '../utils'
import { useDispatch, useSelector } from 'react-redux'
import {
  AbstractMesh,
  ArcRotateCamera,
  Camera,
  Matrix,
  Observer,
  Plane,
  PointerDragBehavior,
  Scene,
  TmpVectors,
  Vector3,
  Viewport,
} from '@babylonjs/core'
import { separateWall } from '../../services/buildingService'
import { planeBoundingBoxIntersection } from '../../utils/MathUtil'

export class SeparateElementUI {
  private static _isUIVisible: boolean

  static get isUIVisible(): boolean {
    return this._isUIVisible
  }

  private readonly color: string = '#FF2F5F'

  private _selectedMesh?: AbstractMesh
  private _wallDirection: Vector3 = new Vector3()
  private _wallNormal: Vector3 = new Vector3()
  private _worldMatrix: Matrix = new Matrix()
  private _pointerDragBehavior?: PointerDragBehavior

  private _scene: Scene
  private _camera: ArcRotateCamera
  private _onViewMatrixChangedObservable?: Observer<Camera>
  private _onProjectionMatrixChangedObservable?: Observer<Camera>

  private _selectionUI: AdvancedDynamicTexture
  private _line: Line
  private _button: Button

  private _splitPoint: number = 0
  private _splitPointVector: Vector3 = new Vector3()
  private _splitPointPlane: Plane = new Plane(1, 0, 0, 0)

  set isVisible(value: boolean) {
    SeparateElementUI._isUIVisible =
      this._line.isVisible =
      this._button.isVisible =
        value

    if (value) {
      this.updateUIPositions()
    }
  }

  get isVisible(): boolean {
    return this._line.isVisible && this._button.isVisible
  }

  get splitPoint(): number {
    const { snapToGrid, cellSize } = store.getState().construction.gridSettings

    if (snapToGrid) {
      const snappendSplitPoint =
        Math.round(this._splitPoint / cellSize) * cellSize
      if (snappendSplitPoint > this._splitPoint) {
        return Math.floor(this._splitPoint / cellSize) * cellSize
      } else {
        return snappendSplitPoint
      }
    } else {
      return this._splitPoint
    }
  }

  constructor() {
    this._line = new Line('SplitWallToolUI_Line')
    this._line.color = this.color
    this._line.lineWidth = 4
    this._line.dash = [24, 4]

    this._button = Button.CreateImageOnlyButton(
      'SplitWallToolUI_Button',
      '/images/icons/spacialSeparateIcon.svg',
    )
    this._button.width = this._button.height = '36px'
    this._button.children[0].width = this._button.children[0].height = '28px'
    this._button.children[0].verticalAlignment =
      Control.VERTICAL_ALIGNMENT_CENTER
    this._button.children[0].horizontalAlignment =
      Control.HORIZONTAL_ALIGNMENT_CENTER
    this._button.color = this._button.background = this.color
    this._button.cornerRadius = 20

    this.isVisible = false
    this._selectionUI = AdvancedDynamicTexture.CreateFullscreenUI(
      'SplitWallToolUI',
    )
      .addControl(this._line)
      .addControl(this._button)

    const sceneManager = SceneManager.getInstance()
    this._scene = sceneManager.getScene()!
    this._camera = sceneManager.getCamera()!
  }

  attachToMesh(
    selectedMesh: AbstractMesh,
    setIsSeparable: Dispatch<SetStateAction<boolean>>,
  ) {
    const wallDirection = selectedMesh.metadata?.wallDirection as
      | Vector3
      | undefined
    const wallNormal = selectedMesh.metadata?.wallNormal as Vector3 | undefined
    if (!wallDirection || !wallNormal) {
      console.error('Wall direction and/or normal is undefined', {
        wallDirection,
        wallNormal,
      })
      throw new Error('Wall direction and/or normal is undefined')
    }

    this._selectedMesh = selectedMesh
    this._wallDirection.copyFrom(wallDirection)
    this._wallNormal.copyFrom(wallNormal)
    Matrix.FromXYZAxesToRef(
      wallDirection,
      Vector3.UpReadOnly,
      wallNormal,
      this._worldMatrix,
    )
    this._worldMatrix.setTranslation(this._selectedMesh.absolutePosition)

    const lastDragPosition = new Vector3()
    const dragDirection = new Vector3()
    this._button.onPointerDownObservable.add((eventData) => {
      if (!this._selectedMesh) {
        return
      }

      this._pointerDragBehavior = new PointerDragBehavior({
        dragAxis: this._wallDirection,
      })
      this._pointerDragBehavior.useObjectOrientationForDragging = false
      this._pointerDragBehavior.moveAttached = false

      this._pointerDragBehavior.onDragStartObservable.add(
        ({ dragPlanePoint }) => {
          setIsSeparable(this.checkIfSeparable())

          lastDragPosition.copyFrom(dragPlanePoint)
        },
      )

      this._pointerDragBehavior.onDragObservable.add(({ dragPlanePoint }) => {
        if (!this._selectedMesh) {
          return
        }

        dragPlanePoint.subtractToRef(lastDragPosition, dragDirection)
        const projection =
          dragDirection.dot(this._wallDirection) /
          this._wallDirection.dot(this._wallDirection)
        const boundingBox = this._selectedMesh.getBoundingInfo().boundingBox
        this._splitPoint = Math.max(
          0,
          Math.min(this._splitPoint + projection, boundingBox.extendSize.x * 2),
        )

        this.updateUIPositions()

        setIsSeparable(this.checkIfSeparable())

        lastDragPosition.copyFrom(dragPlanePoint)
      })

      this._selectedMesh.addBehavior(this._pointerDragBehavior)

      const camera = this._scene.activeCamera
      const pickingRay = this._scene.createPickingRay(
        eventData.x,
        eventData.y,
        null,
        camera,
      )
      this._pointerDragBehavior.startDrag(undefined, pickingRay)
    })

    this._button.onPointerUpObservable.add(() =>
      this.removePointerDragBehavior(),
    )

    this._onViewMatrixChangedObservable =
      this._camera?.onViewMatrixChangedObservable.add(() =>
        this.updateUIPositions(),
      )
    this._onProjectionMatrixChangedObservable =
      this._camera?.onProjectionMatrixChangedObservable.add(() =>
        this.updateUIPositions(),
      )
  }

  detach() {
    this._button.onPointerDownObservable.clear()
    this._button.onPointerUpObservable.clear()

    this.removePointerDragBehavior()

    this._onViewMatrixChangedObservable?.remove()
    this._onProjectionMatrixChangedObservable?.remove()

    this.resetUIPositionAndVisibility()
    this._selectedMesh = undefined
  }

  resetUIPositionAndVisibility() {
    this.isVisible = false
    this._splitPoint = 0
  }

  dispose() {
    this.detach()
    this._line.dispose()
    this._button.dispose()
    this._selectionUI.dispose()
  }

  private checkIfSeparable(): boolean {
    if (!this._selectedMesh) {
      return false
    }

    const boundingBox = this._selectedMesh.getBoundingInfo().boundingBox
    const wallHalfLength = boundingBox.extendSize.x
    this._splitPointVector.copyFrom(this._selectedMesh.position)
    this._wallDirection.scaleAndAddToRef(
      -wallHalfLength + this.splitPoint,
      this._splitPointVector,
    )
    Plane.FromPositionAndNormalToRef(
      this._splitPointVector,
      this._wallDirection,
      this._splitPointPlane,
    )

    const wallOpeningsMeshes = this._selectedMesh.getChildMeshes(
      true,
      (mesh) =>
        mesh instanceof AbstractMesh &&
        (isWindowMesh(mesh) || isDoorMesh(mesh)),
    )

    return wallOpeningsMeshes.every(
      (m) =>
        !planeBoundingBoxIntersection(
          this._splitPointPlane,
          m.getBoundingInfo().boundingBox,
        ),
    )
  }

  private updateUIPositions() {
    const rect = this._scene.getEngine().getRenderingCanvasClientRect()
    if (!this._selectedMesh || !this.isVisible || !rect) {
      return
    }

    const wallToCamera = this._camera.globalPosition.subtractToRef(
      this._selectedMesh.absolutePosition,
      TmpVectors.Vector3[0],
    )
    const isFacingToCamera = Vector3.Dot(this._wallNormal, wallToCamera) > 0

    const viewport = new Viewport(0, 0, rect.width, rect.height)
    const boundingBox = this._selectedMesh.getBoundingInfo().boundingBox

    const elementPosition = TmpVectors.Vector3[1].copyFrom(boundingBox.center)
    elementPosition.x += -boundingBox.extendSize.x + this.splitPoint
    elementPosition.y -= boundingBox.extendSize.y
    elementPosition.z += isFacingToCamera
      ? boundingBox.extendSize.z
      : -boundingBox.extendSize.z

    const elementPositionProjected = Vector3.ProjectToRef(
      elementPosition,
      this._worldMatrix,
      this._scene.getTransformMatrix(),
      viewport,
      TmpVectors.Vector3[2],
    )

    this._line.x1 = elementPositionProjected.x
    this._line.y1 = elementPositionProjected.y

    elementPosition.y += boundingBox.extendSize.y * 2.2 // 2 * 1.1 to make button appear slightly above the wall

    Vector3.ProjectToRef(
      elementPosition,
      this._worldMatrix,
      this._scene.getTransformMatrix(),
      viewport,
      TmpVectors.Vector3[2],
    )

    this._line.x2 = elementPositionProjected.x
    this._line.y2 = elementPositionProjected.y

    this._button.left = -rect.width / 2 + elementPositionProjected.x
    this._button.top = -rect.height / 2 + elementPositionProjected.y

    const min = this._camera.lowerRadiusLimit ?? 5
    const max = this._camera.upperRadiusLimit ?? 20
    const radius = this._camera.radius ?? min

    const t = Math.min(1, Math.max(0, (radius - min) / (max - min)))
    const scale = 1 - t * 0.7

    this._button.scaleX = this._button.scaleY = scale
  }

  private removePointerDragBehavior() {
    if (!this._selectedMesh || !this._pointerDragBehavior) {
      return
    }

    this._pointerDragBehavior.releaseDrag()
    this._selectedMesh.removeBehavior(this._pointerDragBehavior)
    this._pointerDragBehavior = undefined
  }
}

const selectSelectedElementMesh = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.construction.selectedTool,
    (state: RootState) => state.construction.selectedElementsIds,
  ],
  (constructionTab, tool, elementsIds) => {
    const elementId = elementsIds[0]
    if (
      constructionTab !== ConstructionTab.Editor ||
      tool !== BuildingEditingTool.DirectSelect ||
      !elementId ||
      !isValidUUID(elementId)
    ) {
      return null
    }

    const scene = SceneManager.getInstance().getScene()
    const mesh = scene?.getMeshById(elementId)

    if (mesh && isWallMesh(mesh)) {
      return mesh
    }

    return null
  },
)

export const useSeparateWallTool = (): [
  AbstractMesh | null,
  boolean,
  boolean,
  () => void,
] => {
  const dispatch = useDispatch<AppDispatch>()
  const abortControllerRef = useRef<AbortController | null>(null)
  const separateElementUIRef = useRef<SeparateElementUI | null>(null)
  const elementToSeparate = useSelector(selectSelectedElementMesh)
  const [isSeparable, setIsSeparable] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const ui = new SeparateElementUI()
    separateElementUIRef.current = ui

    return () => {
      ui.dispose()
    }
  }, [])

  useEffect(() => {
    setIsSeparable(true)

    const ui = separateElementUIRef.current
    if (!ui || !elementToSeparate) {
      return
    }

    ui.attachToMesh(elementToSeparate, setIsSeparable)

    return () => {
      abortControllerRef.current?.abort()
      ui.detach()
    }
  }, [elementToSeparate])

  const separateElement = useCallback(() => {
    const ui = separateElementUIRef.current
    if (!elementToSeparate || !ui || isLoading) {
      return
    }

    if (ui.isVisible) {
      const splitPoint = ui.splitPoint
      const boundingBox = elementToSeparate.getBoundingInfo().boundingBox
      const wallWidth = boundingBox.extendSize.x * 2

      ui.resetUIPositionAndVisibility()
      if (
        Math.abs(splitPoint) > 1e-3 &&
        Math.abs(wallWidth - splitPoint) > 1e-3
      ) {
        const abortController = new AbortController()
        abortControllerRef.current = abortController

        dispatch(
          separateWall({
            wallId: elementToSeparate.id,
            signal: abortController.signal,
            splitPoint,
            setIsLoading,
          }),
        )
      }
    } else {
      ui.isVisible = true
    }
  }, [dispatch, elementToSeparate, isLoading])

  return [elementToSeparate, isSeparable, isLoading, separateElement]
}
